import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import { getGame, makeMove, abandonGame } from '../../services/gameApi';
import { initializeGameWebSocket, subscribeToGame, sendMove, disconnectGameWebSocket, isConnected } from '../../services/gameWebSocketManager';
import './TicTacToe.css';

const TicTacToe = () => {
  const { gameId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);

  const loadGame = async () => {
    try {
      const response = await getGame(gameId);
      setGame(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGame();
    
    // Only initialize WebSocket if user is authenticated
    // Anonymous users playing against computer don't need WebSocket
    if (user) {
      initializeGameWebSocket();
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      // Only disconnect if we actually connected
      if (user) {
        disconnectGameWebSocket();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, user]);

  useEffect(() => {
    // Only subscribe to WebSocket if user is authenticated
    // Anonymous users playing against computer don't need WebSocket updates
    if (gameId && user) {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      
      // subscribeToGame is now async, handle it properly
      subscribeToGame(gameId, (updatedGame) => {
        setGame(updatedGame);
      }).then((subscription) => {
        if (subscription) {
          subscriptionRef.current = subscription;
        }
      }).catch((error) => {
        console.error('Failed to subscribe to game:', error);
      });
    } else if (subscriptionRef.current) {
      // Clean up subscription if user logs out or gameId changes
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [gameId, user]); // Re-subscribe when gameId or user changes

  const handleCellClick = async (row, col) => {
    if (!game || game.status !== 'IN_PROGRESS') return;
    
    // Check if it's the current user's turn (handles both authenticated and anonymous users)
    const isMyTurn = user?.username 
      ? game.currentPlayer === user.username
      : game.currentPlayer === game.player1Username; // For anonymous users, check against player1Username
    
    if (!isMyTurn) return;
    if (game.board[row][col] !== null) return;

    try {
      // Only use WebSocket if user is authenticated and connected
      // Anonymous users and computer games use REST API
      if (user && isConnected()) {
        const playerUsername = user.username;
        sendMove(gameId, playerUsername, row, col);
        // WebSocket will broadcast update, subscription will handle it
      } else {
        // Use REST API for anonymous users or when WebSocket is not available
        const response = await makeMove(gameId, row, col);
        // Backend makes computer move synchronously for computer games
        // Response should include both user move and computer move
        setGame(response.data);
        
        // For computer games, reload after a brief delay to ensure state is synced
        // This handles any potential timing issues with computer move
        if (response.data.gameType === 'COMPUTER' && response.data.status === 'IN_PROGRESS') {
          setTimeout(() => {
            loadGame();
          }, 300);
        }
      }
    } catch (err) {
      console.error('Move error:', err);
      setError(err.response?.data?.error || 'Failed to make move');
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to abandon this game?')) return;
    
    try {
      await abandonGame(gameId);
      navigate('/game/tic-tac-toe');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to abandon game');
    }
  };

  const getStatusMessage = () => {
    if (!game) return '';
    
    if (game.status === 'FINISHED') {
      if (game.winner === 'DRAW') {
        return 'Game ended in a draw!';
      } else if (game.winner === game.player1Username) {
        const displayName = game.player1DisplayName || game.player1Username;
        return `${displayName} wins!`;
      } else if (game.winner === 'COMPUTER' || game.winner === game.player2Username) {
        return game.player2Username ? `${game.player2Username} wins!` : 'Computer wins!';
      } else if (game.winner) {
        // Fallback for any other winner value
        return `${game.winner} wins!`;
      }
    } else if (game.status === 'ABANDONED') {
      return 'Game abandoned';
    } else {
      // Check if it's the current user's turn (handles both authenticated and anonymous users)
      const isMyTurn = user?.username 
        ? game.currentPlayer === user.username
        : game.currentPlayer === game.player1Username; // For anonymous users, check against player1Username
      
      if (isMyTurn) {
        return 'Your turn!';
      } else if (game.currentPlayer === 'COMPUTER') {
        return 'Computer is thinking...';
      } else {
        return `Waiting for ${game.currentPlayer}...`;
      }
    }
  };

  if (loading) {
    return <div className="tic-tac-toe-container"><div className="loading">Loading game...</div></div>;
  }

  if (error || !game) {
    return (
      <div className="tic-tac-toe-container">
        <div className="error">{error || 'Game not found'}</div>
        <button onClick={() => navigate('/game/tic-tac-toe')} className="btn-back">Back to Games</button>
      </div>
    );
  }

  return (
    <div className="tic-tac-toe-container">
      <BackButton />
      <div className="game-header">
        <div className="game-info">
          <h2>Tic Tac Toe</h2>
          <div className="players">
            <div className={`player ${game.currentPlayer === game.player1Username ? 'active' : ''}`}>
              <span className="player-name">{game.player1DisplayName || game.player1Username}</span>
              <span className="player-symbol">X</span>
            </div>
            <span className="vs">VS</span>
            <div className={`player ${game.currentPlayer === (game.player2Username || 'COMPUTER') ? 'active' : ''}`}>
              <span className="player-name">{game.player2Username || 'Computer'}</span>
              <span className="player-symbol">O</span>
            </div>
          </div>
        </div>
        <button onClick={handleAbandon} className="btn-abandon">Abandon</button>
      </div>

      <div className="game-status">{getStatusMessage()}</div>

      <div className="game-board">
        {[0, 1, 2].map(row => (
          <div key={row} className="board-row">
            {[0, 1, 2].map(col => {
              const cellValue = game.board[row][col];
              // Check if it's the current user's turn (handles both authenticated and anonymous users)
              const isMyTurn = user?.username 
                ? game.currentPlayer === user.username
                : game.currentPlayer === game.player1Username; // For anonymous users, check against player1Username
              const isClickable = game.status === 'IN_PROGRESS' && 
                                  isMyTurn && 
                                  cellValue === null;
              
              return (
                <div
                  key={col}
                  className={`board-cell ${isClickable ? 'clickable' : ''} ${cellValue ? `filled-${cellValue.toLowerCase()}` : ''}`}
                  onClick={() => handleCellClick(row, col)}
                >
                  {cellValue}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {game.status === 'FINISHED' && (
        <button onClick={() => navigate('/game/tic-tac-toe')} className="btn-new-game">
          Back to Games
        </button>
      )}
    </div>
  );
};

export default TicTacToe;

